import { z } from "@/zod";
import {
  ulid,
  settlementType,
  nonNegativeDecimalStringSchema,
  createNullableBoundedNumericStringSchema,
} from "@/schemas/commonFields";
import { HOURS_WORKED_CORRECTED_MAX } from "@/constants";
import { taxStatus } from "@/schemas/commonFields/taxStatus";
import { AGREEMENT_STATUS } from "@/types/agreement";
import { applyEmptyStringsToNulls } from "@/lib/utils/zod";

const payrollRoleBasedInspectionBase = z.object({
  payroll_id: ulid,
  period: z.string().date(),
  project_id: ulid,
  project: z.string(),
  manager_id: ulid,
  manager: z.string(),
  regional_director_id: ulid,
  regional_director: z.string(),
  regional_representative_id: ulid,
  regional_representative: z.string(),
  store_id: ulid,
  store: z.string(),
  project_role: z.string(),
  contractor_id: ulid,
  contractor: z.string(),
  personal_id_number: z.string(),
  agreement_company_id: ulid,
  agreement_company: z.string(),
  agreement_status_period: z.enum(AGREEMENT_STATUS),
  agreement_status_current: z.enum(AGREEMENT_STATUS),
  permanent_comment: z.string(),
  tax_status_period: taxStatus,
  tax_status_current: taxStatus,
  tax_status_corrected: taxStatus.nullable(),
  tax_status_final: taxStatus,
  settlement_type: z.union([settlementType, z.literal("")]),
  hourly_rate: nonNegativeDecimalStringSchema,
  visit_fee: nonNegativeDecimalStringSchema,
  monthly_rate: nonNegativeDecimalStringSchema,
  total_kam_planned_duration: nonNegativeDecimalStringSchema,
  kam_planned_duration: nonNegativeDecimalStringSchema,
  rr_planned_duration: nonNegativeDecimalStringSchema,
  total_hours_worked: nonNegativeDecimalStringSchema,
  hours_worked: nonNegativeDecimalStringSchema,
  hours_worked_corrected: nonNegativeDecimalStringSchema,
  hours_worked_final: nonNegativeDecimalStringSchema,
  contractor_card_s3_key: z.string(),
  contractor_card_mimetype: z.string(),
  contractor_card_filename: z.string(),
  total_visit_count: nonNegativeDecimalStringSchema,
  visit_count: nonNegativeDecimalStringSchema,
  visit_count_corrected: nonNegativeDecimalStringSchema,
  visit_count_final: nonNegativeDecimalStringSchema,
  base_amount_to_pay: nonNegativeDecimalStringSchema,
  deduction: nonNegativeDecimalStringSchema,
  penalty: nonNegativeDecimalStringSchema,
  addition: nonNegativeDecimalStringSchema,
  bonus: nonNegativeDecimalStringSchema,
  amount_to_pay: nonNegativeDecimalStringSchema,
  total_limit_hours: nonNegativeDecimalStringSchema,
  total_invoiced_hours: nonNegativeDecimalStringSchema,
  total_invoiced_addition: nonNegativeDecimalStringSchema,
  total_invoiced_deduction: nonNegativeDecimalStringSchema,
  total_invoiced_amount: nonNegativeDecimalStringSchema,
  total_invoiced_hours_exceeded: nonNegativeDecimalStringSchema,
  comment: z.string(),
  field_account_manager_id: z.string(),
  field_account_manager: z.string(),
  student_id_valid_until: z.string(),
  is_employed: z.boolean(),
  is_student: z.boolean(),
  citizenship: z.string(),
  hours_bound_to_visits: z.boolean().nullable(),
  most_frequent_planned_visit_duration:
    nonNegativeDecimalStringSchema.nullable(),
});

export const payrollRoleBasedInspectionPreview = payrollRoleBasedInspectionBase
  .extend({
    id: z.string(), // Not uuid. Previews are not real entries and are generated on the fly
  })
  .readonly();

export const payrollRoleBasedInspection = payrollRoleBasedInspectionBase
  .extend({
    id: ulid,
  })
  .readonly();

export const updatePayrollRoleBasedInspection = applyEmptyStringsToNulls({
  id: ulid,
  settlement_type: z.union([settlementType, z.literal("")]).nullable(),
  hours_worked_corrected: createNullableBoundedNumericStringSchema({
    max: HOURS_WORKED_CORRECTED_MAX,
  }),
  visit_count_corrected: z.coerce.number().nullable(),
  deduction: nonNegativeDecimalStringSchema.nullable(),
  penalty: nonNegativeDecimalStringSchema.nullable(),
  addition: nonNegativeDecimalStringSchema.nullable(),
  bonus: nonNegativeDecimalStringSchema.nullable(),
  comment: z.coerce.string().max(250).nullable(),
  tax_status_corrected: taxStatus.nullable(),
});
